import React, { useState } from "react";
import {
  Alignment,
  AnchorButton,
  Navbar,
  NavbarGroup,
  NavbarDivider,
  Button,
  Popover,
  Tooltip,
  Icon,
  Menu,
  MenuDivider,
  MenuItem
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { Link, useLocation } from "react-router-dom";
import CreateSimulationModal from "./CreateSimulationModal";
import { ThirdPartyLicensesDialog } from "./ThirdPartyLicensesDialog";
import { useAuth } from "react-oidc-context";
import { getNormalizedAuthClaims } from "../auth/authClaims";
import logo from "../assets/logo-horizontal.svg";

export interface MainNavigationProps { }

// Library packages published on download.cppmodel.com. Bump the version on
// release; there is one package per OS and architecture.
const DOWNLOAD_URL = "https://download.cppmodel.com";
const CPPMODEL_VERSION = "0.10.0";
const PACKAGES = [
  { text: "Linux x86_64 (.tar.gz)", file: "Linux-x86_64.tar.gz" },
  { text: "Linux aarch64 (.tar.gz)", file: "Linux-aarch64.tar.gz" },
  { text: "Windows x64 (.zip)", file: "Windows-x64.zip" },
  { text: "Windows ARM64 (.zip)", file: "Windows-ARM64.zip" },
  { text: "macOS universal (.tar.gz)", file: "macOS-universal.tar.gz" },
];

export const MainNavigation: React.FC<MainNavigationProps> = () => {
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLicensesOpen, setIsLicensesOpen] = useState(false);
  const auth = useAuth();
  const authClaims = getNormalizedAuthClaims();
  const email = auth.user?.profile.email ?? authClaims.email;
  const firstName = auth.user?.profile.given_name ?? authClaims.givenName;
  const isWorkspaceAdmin = authClaims.isWorkspaceAdmin;

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const openUserProfile = () => {
    if (auth.user?.profile.iss) {
      const keycloakUrl = auth.user.profile.iss; // issuer URL from user profile
      const profileUrl = `${keycloakUrl}/account`; // Keycloak profile URL
      window.open(profileUrl, '_blank');
    }
  };
  // Determine the active route for highlighting the Results button
  const isResultsPage = location.pathname.startsWith('/results');
  const isAllSimulationsPage = location.pathname === '/';
  const isUserAdministrationPage = location.pathname === '/users';

  const downloadMenu = <Menu>
    <MenuDivider title={`CppModel ${CPPMODEL_VERSION}`} />
    {PACKAGES.map(({ text, file }) => (
      <MenuItem key={file} text={text} href={`${DOWNLOAD_URL}/CppModel-${CPPMODEL_VERSION}-${file}`} />
    ))}
    <MenuDivider title="Tools" />
    <MenuItem
      text={<>
        AI agent plugin
        <div className="cm-menu-subtext">Claude Code, OpenAI Codex, GitHub Copilot CLI, Cursor, Gemini CLI</div>
      </>}
      multiline
      labelElement={<span className="cm-menu-external">GitHub <Icon icon={IconNames.Share} size={12} /></span>}
      href="https://github.com/Control-Edge/cppmodel-claude"
      target="_blank"
    />
    {/* <MenuItem text="Linux (.tar.gz)" href="#" />
    <MenuItem text="Windows (.exe)" href="#" />
    <MenuItem text="Mac (.dmg)" href="#" /> */}
  </Menu>
  // const newSimulationMenu = <Menu>
  //   <MenuItem text="Simulation" onClick={openModal} />
  //   {/* <MenuItem text="CMake Project" /> */}
  // </Menu>
  const userMenu = <Menu>
    <MenuItem text="Profile" onClick={openUserProfile} target="_blank" />
    <MenuItem text="Billing" href="https://billing.stripe.com/p/login/cNi7sL1twaZm61Qa5eao800" target="_blank" />
  </Menu>

  const helpMenu = <Menu>
    <MenuItem text="Online Documentation" href="https://www.cppmodel.com/docs/Overview" target="_blank" />
    <MenuItem text="Contact Support" href="mailto:support@cedge.se?subject=Workspace%20Support%20Request" target="_blank" />
    <MenuItem text="Copy support email" onClick={() => navigator.clipboard.writeText('support@cedge.se')} />
    <MenuItem text="Join support Discord" href="https://discord.gg/TmbcMbpbns" target="_blank" />
    <MenuDivider />
    <MenuItem text="Third-party licenses" icon={IconNames.Document} onClick={() => setIsLicensesOpen(true)} />
  </Menu>

  return (
    <>
      <Navbar className="cm-navbar">
        <NavbarGroup align={Alignment.LEFT}>
          <Link to="/" className="cm-navbar-brand">
            <img src={logo} alt="CppModel" className="cm-navbar-logo" />
            <span className="cm-navbar-product">Workspace</span>
          </Link>
          <NavbarDivider />
          <Link to="/">
            <Button
              text="All Simulations"
              active={isAllSimulationsPage}
              minimal
              rightIcon={IconNames.Home}
            />
          </Link>
          <Button
            text="Results"
            disabled={!isResultsPage}
            active={isResultsPage}
            minimal
            rightIcon={IconNames.TimelineLineChart}
          />
          {/* <Popover content={newSimulationMenu}> */}
          <Button
            text="New"
            minimal
            rightIcon={IconNames.SeriesAdd}
            onClick={openModal}
          />
          {/* </Popover> */}
          <NavbarDivider />
          <Tooltip content="View online documentation and support options">
            <Popover content={helpMenu}>
              <Button
                text="Help"
                minimal
                rightIcon={IconNames.Lifesaver}
              />
            </Popover>
          </Tooltip>
          <Tooltip content="Open algorithm playground in new window">
            <AnchorButton
              text="Playground"
              href="https://playground.cppmodel.com"
              target="_blank"
              minimal
              rightIcon={IconNames.Share}
            />
          </Tooltip>
          <Tooltip content="Download CppModel libraries and tools">
            <Popover content={downloadMenu}>
              <Button
                text="Download"
                minimal
                rightIcon={IconNames.Download}
              />
            </Popover>
          </Tooltip>
          {isWorkspaceAdmin && (
            <>
              <NavbarDivider />
              <Link to="/users">
                <Button
                  text="User Administration"
                  active={isUserAdministrationPage}
                  minimal
                  rightIcon={IconNames.People}
                />
              </Link>
            </>
          )}
        </NavbarGroup>
        <NavbarGroup align={Alignment.RIGHT}>
          <Tooltip content="View user profile">
            <Popover content={userMenu}>
              <Button
                text={`${firstName ? firstName : email}`}
                minimal
                rightIcon={IconNames.User}
              />
            </Popover>
          </Tooltip>
          <NavbarDivider />
          <AnchorButton
            text="Logout"
            onClick={() => auth.signoutRedirect()}
            minimal
            rightIcon={IconNames.LogOut}
          />
        </NavbarGroup>
      </Navbar>
      <CreateSimulationModal isOpen={isModalOpen} onClose={closeModal} />
      <ThirdPartyLicensesDialog isOpen={isLicensesOpen} onClose={() => setIsLicensesOpen(false)} />
    </>
  );
};
